import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type ColorMode = 'light' | 'dark';

export const COLOR_MODE_STORAGE_KEY = 'mir-ai.colorMode';

export const isColorMode = (v: unknown): v is ColorMode => v === 'light' || v === 'dark';

function detectSystemPreference(): ColorMode {
  try {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
  } catch {
    /* matchMedia unavailable — fall through */
  }
  return 'light';
}

export function loadInitialColorMode(): ColorMode {
  try {
    const raw = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    if (isColorMode(raw)) return raw;
  } catch {
    /* storage unavailable — fall through */
  }
  return detectSystemPreference();
}

interface ColorModeState {
  mode: ColorMode;
}

const initialState: ColorModeState = { mode: loadInitialColorMode() };

const slice = createSlice({
  name: 'colorMode',
  initialState,
  reducers: {
    setColorMode(state, action: PayloadAction<ColorMode>) {
      if (state.mode === action.payload) return;
      state.mode = action.payload;
      try {
        localStorage.setItem(COLOR_MODE_STORAGE_KEY, action.payload);
      } catch {
        /* storage unavailable — ignore */
      }
    },
    toggleColorMode(state) {
      const next: ColorMode = state.mode === 'dark' ? 'light' : 'dark';
      state.mode = next;
      try {
        localStorage.setItem(COLOR_MODE_STORAGE_KEY, next);
      } catch {
        /* storage unavailable — ignore */
      }
    },
  },
});

export const { setColorMode, toggleColorMode } = slice.actions;
export const colorModeReducer = slice.reducer;
export type { ColorModeState };
